import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import { notifyTimerChanged } from '../components/tracker/Timer/TimerBar';
import DetailPane from '../components/Todo/DetailPane';
import AiHandoffSheet from '../components/Todo/AiHandoffSheet';
import TaskContextMenu, { type MenuItem } from '../components/Todo/TaskContextMenu';
import Sidebar from '../components/Todo/Sidebar';
import TaskListPane from '../components/Todo/TaskListPane';
import TodoLayout from '../layouts/TodoLayout';
import useTaskSearch from '../hooks/useTaskSearch';
import api, { ApiError } from '../lib/api';
import type { HandoffTarget } from '../types/aiHandoff';
import type { TodoPageProps, TodoTask, TodoView } from '../types/todo';

// Exclude 'trash'：回收站是独立页面（TodoTrash），不走三栏布局，
// 这张表是六个列表视图的标题文案，给它编一条永不渲染的条目不如说清楚。
export const VIEW_META: Record<Exclude<TodoView, 'trash'>, { name: string; sub: string }> = {
  today: { name: '今天', sub: '只放今天真的要做的事' },
  tomorrow: { name: '明天', sub: '今天塞不下、但明天要做的' },
  important: { name: '重要', sub: 'important = true，跨 Q1 + Q2' },
  backlog: { name: '待办池', sub: '没排期，不排红，不催你' },
  recent: { name: '近期', sub: '未完成的全部 + 今天完成的' },
  project: { name: '项目', sub: '' },
};

type TodoPage = React.FC<TodoPageProps> & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

const Todo: TodoPage = ({
  view,
  project,
  todo,
  counts,
  projects,
  categories,
  facts,
  running_entry,
  rolled_over,
}) => {
  const [tasks, setTasks] = useState<TodoTask[]>(todo.tasks);
  // 右键菜单。位置存在页面级 state 里而不是行里 —— 中栏是 overflow-y-auto 的
  // 滚动容器，菜单渲染在行内会被裁掉。
  const [menu, setMenu] = useState<{ task: TodoTask; x: number; y: number } | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // 「扔给 AI」浮层的作用对象。右键菜单可能作用在没被选中的那一条上，
  // 所以它是独立的一份 state，不复用 selectedId。
  const [handoff, setHandoff] = useState<HandoffTarget | null>(null);
  const [capBlocked, setCapBlocked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 退回告知看过就关掉。整页加载会带来新的 rolled_over，那时再显示一次。
  const [rolloverDismissed, setRolloverDismissed] = useState(false);
  // 抽屉只在 < 640px 存在，是纯 UI 状态，不进 URL——与「选中的任务不进 URL」
  // （三栏页 spec §1 非目标 7）同一个取舍。
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const addInputRef = useRef<HTMLInputElement | null>(null);
  const sideSearchRef = useRef<HTMLInputElement | null>(null);
  const listSearchRef = useRef<HTMLInputElement | null>(null);

  // 局部重载后 prop 变了，本地副本跟着重置。
  useEffect(() => {
    setTasks(todo.tasks);
  }, [todo.tasks]);

  // 切视图时清掉选中和黄框——选中的那条任务可能不在新视图里。
  useEffect(() => {
    setSelectedId(null);
    setCapBlocked(false);
    setError(null);
  }, [view, project?.id]);

  useEffect(() => {
    setRolloverDismissed(false);
  }, [rolled_over]);

  const refresh = useCallback(() => {
    router.reload({ only: ['todo', 'counts', 'facts', 'running_entry'] });
  }, []);

  // 当前这一栏的地址与名字。清掉搜索词要回到**开始搜之前**那一栏 ——
  // 从「今天」搜了一圈之后被丢在待办池，那是把一次查看变成了一次导航。
  const here = useMemo(
    () => ({
      href: view === 'project' && project ? `/todo/projects/${project.id}` : `/todo/${view}`,
      name: view === 'project' ? (project?.name ?? '项目') : VIEW_META[view].name,
    }),
    [view, project]
  );
  const [origin, setOrigin] = useState(here);
  useEffect(() => {
    if (!todo.q) setOrigin(here);
  }, [here, todo.q]);

  const { draft: searchDraft, setDraft: setSearchDraft, clear: clearSearch } =
    useTaskSearch({ activeQuery: todo.q, originHref: origin.href });

  const patchTask = useCallback(
    async (id: number, patch: Record<string, unknown>, opts: { capAware?: boolean } = {}) => {
      setError(null);
      try {
        await api.tasks.update(id, patch);
        setCapBlocked(false);
        refresh();
        return true;
      } catch (e) {
        // 上限 422 时显示原型那段黄框，重点是「先关掉一条」而不是「操作失败」
        if (opts.capAware && e instanceof ApiError && e.status === 422) {
          setCapBlocked(true);
        } else {
          setError(e instanceof Error ? e.message : '更新失败');
        }
        return false;
      }
    },
    [refresh]
  );

  // 高频动作走乐观更新：先改本地再发请求，失败回滚。
  // 勾选完成、加星、切紧急都属于「点一下就想立刻看到」的操作，等一次往返太钝。
  const patchOptimistic = useCallback(
    async (task: TodoTask, patch: Partial<TodoTask>) => {
      const rollback = Object.fromEntries(
        Object.keys(patch).map((key) => [key, task[key as keyof TodoTask]])
      ) as Partial<TodoTask>;

      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, ...patch } : t)));
      setError(null);
      try {
        await api.tasks.update(task.id, patch as Record<string, unknown>);
        refresh();
      } catch (e) {
        // 取消完成会让这条任务重新占额度，可能撞上限——回滚并说明原因。
        setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, ...rollback } : t)));
        setError(e instanceof Error ? e.message : '更新失败');
      }
    },
    [refresh]
  );

  const toggleComplete = useCallback(
    (task: TodoTask) => patchOptimistic(task, { completed: !task.completed }),
    [patchOptimistic]
  );

  const toggleImportant = useCallback(
    (task: TodoTask) => patchOptimistic(task, { important: !task.important }),
    [patchOptimistic]
  );

  const createTask = useCallback(
    async (title: string) => {
      const payload: Record<string, unknown> = { title };
      // 在哪个视图里加，就带上那个视图的属性——加完立刻能看见，不会凭空消失
      if (view === 'today') payload.planned_for = todo.today;
      if (view === 'important') payload.important = true;
      if (view === 'project' && project) payload.project_id = project.id;

      setError(null);
      try {
        await api.tasks.create(payload);
        setCapBlocked(false);
        refresh();
      } catch (e) {
        if (view === 'today' && e instanceof ApiError && e.status === 422) {
          setCapBlocked(true);
        } else {
          setError(e instanceof Error ? e.message : '创建失败');
        }
      }
    },
    [view, project, todo.today, refresh]
  );

  const stopTimer = useCallback(async () => {
    setError(null);
    try {
      await api.timeEntries.stop();
      // 让 /tracker/plan 那边的计时条也跟着变——它们监听同一个事件
      notifyTimerChanged();
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : '停止失败');
    }
  }, [refresh]);

  const startTimer = useCallback(
    async (taskId: number) => {
      setError(null);
      try {
        await api.timeEntries.start({ task_id: taskId });
        notifyTimerChanged();
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : '开始计时失败');
      }
    },
    [refresh]
  );

  const destroyTask = useCallback(
    async (id: number) => {
      setError(null);
      try {
        await api.tasks.delete(id);
        setSelectedId((current) => (current === id ? null : current));
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : '删除失败');
      }
    },
    [refresh]
  );

  // 步骤就是带 parent_id 的子任务。不传 planned_for —— 新建的步骤是一件还没
  // 排期的事，它落进待办池。（曾经的理由是「子任务不占额度」，平权之后不成立了：
  // 子任务照样占额度，只是没有 planned_for 的不占任何一天。）
  const createStep = useCallback(
    async (parentId: number, title: string) => {
      setError(null);
      try {
        await api.tasks.create({ title, parent_id: parentId });
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : '添加步骤失败');
      }
    },
    [refresh]
  );

  // 挂一条已有任务：写的是**那一条**的 parent_id。不传 planned_for —— 它自己的
  // 排期原样保留，而平权之后额度只跟 planned_for 有关、与 parent_id 无关，所以
  // 挂上和解绑都不改变占用。
  const linkExisting = useCallback(
    async (childId: number) => {
      if (selectedId === null) return;
      setError(null);
      try {
        await api.tasks.update(childId, { parent_id: selectedId });
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : '挂上失败');
      }
    },
    [selectedId, refresh]
  );

  const unlinkParent = useCallback(async () => {
    if (selectedId === null) return;
    setError(null);
    try {
      await api.tasks.update(selectedId, { parent_id: null });
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : '解绑失败');
    }
  }, [selectedId, refresh]);

  const toggleStep = useCallback(
    (stepId: number, completed: boolean) => {
      void patchTask(stepId, { completed });
    },
    [patchTask]
  );

  const renameStep = useCallback(
    (stepId: number, title: string) => {
      void patchTask(stepId, { title });
    },
    [patchTask]
  );

  // 步骤删除不做二次确认：它比任务轻得多，而且步骤没有下级会被连带删掉。
  const deleteStep = useCallback(
    async (stepId: number) => {
      setError(null);
      try {
        await api.tasks.delete(stepId);
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : '删除步骤失败');
      }
    },
    [refresh]
  );

  // 拖拽排序。收到的是「当前视图未完成任务的新顺序」，但**不能只提交这一批**：
  // Task.reorder! 会把提交的 id 的 position 重排成 0..size-1 的连续值，它的
  // 前提是提交整份待办列表。只交子集的话，这批会被压到一个低区间，视图外
  // 任务的相对顺序跟着乱——在「今天」拖一下，待办池的顺序会变。
  //
  // 所以拿服务端给的全量顺序 order_ids，把视图内的新顺序按原槽位填回去：
  // 视图外的 id 一个都不动，视图内的槽位依次换成新顺序。
  const reorder = useCallback(
    async (viewOrder: number[]) => {
      const inView = new Set(viewOrder);
      let cursor = 0;
      const merged = todo.order_ids.map((id) => (inView.has(id) ? viewOrder[cursor++] : id));

      // 本地先按新顺序排好，不然要等一次往返才看到结果——拖拽必须是即时的。
      // 不在 viewOrder 里的（已完成的那些）统一给一个有限的大值：用 Infinity
      // 的话两个都不在时相减得 NaN，比较器返回 NaN 是未定义行为。
      const rank = new Map(viewOrder.map((id, index) => [id, index]));
      const rankOf = (id: number) => rank.get(id) ?? Number.MAX_SAFE_INTEGER;
      setTasks((prev) => [...prev].sort((a, b) => rankOf(a.id) - rankOf(b.id)));

      setError(null);
      try {
        await api.tasks.reorder(merged);
        refresh();
      } catch (e) {
        setError(e instanceof Error ? e.message : '排序失败');
        refresh();
      }
    },
    [todo.order_ids, refresh]
  );

  // 「明天」的日期串。不从 counts 拿 —— 那边只有条数。
  const tomorrow = useMemo(() => {
    const date = new Date(`${todo.today}T00:00:00`);
    date.setDate(date.getDate() + 1);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }, [todo.today]);

  // 菜单里有哪几项、每项干什么 —— 业务规则放这里（持有全部 handler 的地方），
  // TaskContextMenu 只管怎么显示和操作。每一项都接现成的 handler，后端零改动。
  const menuItems = useCallback(
    (task: TodoTask): MenuItem[] => {
      const inToday = task.planned_for === todo.today;
      const inTomorrow = task.planned_for === tomorrow;
      // 放第一项：其余每一项都是改这条任务的属性，只有它把你带出这个页面。
      const items: MenuItem[] = [
        { key: 'handoff', label: '✦ 扔给 AI', onSelect: () => setHandoff({ kind: 'task', task }) },
      ];

      // 已完成的行在折叠组里，position 对它没有意义
      if (!task.completed) {
        items.push({
          key: 'top',
          label: '移到顶部',
          onSelect: () => {
            // reorder 要的是「本视图未完成行的新顺序」，与拖拽走的是同一条路
            const openIds = tasks.filter((item) => !item.completed).map((item) => item.id);
            reorder([task.id, ...openIds.filter((id) => id !== task.id)]);
          },
        });
      }

      items.push({
        key: 'today',
        label: inToday ? '移出「今天」' : '加入「今天」',
        onSelect: () =>
          patchTask(task.id, { planned_for: inToday ? null : todo.today }, { capAware: !inToday }),
      });
      items.push({
        key: 'tomorrow',
        label: inTomorrow ? '移出「明天」' : '移到「明天」',
        onSelect: () =>
          patchTask(task.id, { planned_for: inTomorrow ? null : tomorrow }, { capAware: !inTomorrow }),
      });
      // 只在排了别的日期时才给这一项 —— 排在今天/明天时上面那两条的「移出」
      // 做的是同一件事（planned_for = null），两项并列会让人以为有区别。
      if (task.planned_for && !inToday && !inTomorrow) {
        items.push({
          key: 'backlog',
          label: '移到待办池',
          onSelect: () => patchTask(task.id, { planned_for: null }),
        });
      }

      items.push({
        key: 'important',
        label: task.important ? '取消重要' : '标为重要',
        onSelect: () => patchOptimistic(task, { important: !task.important }),
      });
      items.push({
        key: 'complete',
        label: task.completed ? '取消完成' : '标记完成',
        onSelect: () => toggleComplete(task),
      });
      items.push({
        key: 'destroy',
        label: '删除',
        danger: true,
        confirm: '确认删除？',
        onSelect: () => destroyTask(task.id),
      });

      return items;
    },
    [
      tasks,
      todo.today,
      tomorrow,
      reorder,
      patchTask,
      patchOptimistic,
      toggleComplete,
      destroyTask,
    ]
  );

  // 全局快捷键。正在输入时一律不抢——Esc 在输入框里是「撤销这次编辑」，
  // n 在输入框里就是字母 n。
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const el = document.activeElement;
      const typing = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;

      // ⌘K / Ctrl+K 排在 typing 守卫之前 —— 它带修饰键，与下面 n / a / Esc
      // 那三条不同族，正在输入时也该抢得到。送的是左栏那个框：它在任何
      // 视图里都在，而中栏那个只有待办池才有。不开任何浮层。
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        sideSearchRef.current?.focus();
        sideSearchRef.current?.select();
        return;
      }

      // Esc 在两个搜索框里都是「清掉这次搜索」。这里没有能「关」的东西，
      // 所以 Esc 只有这一个含义。
      if (
        event.key === 'Escape' &&
        (el === sideSearchRef.current || el === listSearchRef.current)
      ) {
        event.preventDefault();
        clearSearch();
        return;
      }

      if (event.key === 'Escape' && !typing) {
        setSelectedId(null);
        return;
      }

      if (event.key === 'n' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        addInputRef.current?.focus();
      }

      // 不带修饰键，与 n / Esc 同族。目标是当前选中的那一条 —— 没选中就什么
      // 都不做，不去猜。
      if (event.key === 'a' && !typing && !event.metaKey && !event.ctrlKey && !event.altKey) {
        const target = tasks.find((item) => item.id === selectedId);
        if (target) {
          event.preventDefault();
          setHandoff({ kind: 'task', task: target });
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // tasks / selectedId 进依赖：这个 handler 要读它们当下的值。原来是空依赖
    // 数组，因为那时 body 里只有 setter 和 ref。
  }, [tasks, selectedId, clearSearch]);

  const selected = useMemo(
    () => tasks.find((task) => task.id === selectedId) ?? null,
    [tasks, selectedId]
  );

  // 搜索态下标题不叫「待办池」：那一栏的口径是 planned_for IS NULL AND
  // completed = FALSE，而搜索范围含已完成与步骤，两类都不属于它。
  // 位置是待办池的，内容不冒充待办池。
  const title = todo.q
    ? `搜索「${todo.q}」`
    : view === 'project'
      ? (project?.name ?? '项目')
      : VIEW_META[view].name;
  const sub = todo.q
    ? `命中标题、备注和留言 · 含已完成和步骤 · 回收站不搜${
        origin.href === '/todo/backlog' ? '' : `　·　✕ 清掉回到「${origin.name}」`
      }`
    : view === 'project'
      ? ''
      : VIEW_META[view].sub;

  // 两个框共用这一份。null 只在「这个视图不放框」时传给中栏。
  const searchBox = {
    draft: searchDraft,
    onChange: setSearchDraft,
    onClear: clearSearch,
  };

  return (
    <div
      data-todo-grid
      className={[
        // mobile-first：base 是单栏（侧栏此时是覆盖式抽屉，不占列）。
        // sm: 之后恢复本来的两栏，wide: 之后三栏——这两档与改造前逐像素一致。
        'relative grid h-full grid-cols-[minmax(0,1fr)] sm:grid-cols-[220px_minmax(0,1fr)]',
        selected
          ? 'wide:grid-cols-[252px_minmax(0,1fr)_330px]'
          : 'wide:grid-cols-[252px_minmax(0,1fr)_0px]',
      ].join(' ')}
    >
      <Sidebar
        view={view}
        projectId={project?.id ?? null}
        counts={counts}
        projects={projects}
        facts={facts}
        today={todo.today}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onHandoffList={(v, pid, name, count) =>
          setHandoff({ kind: 'list', view: v, projectId: pid, name, count })
        }
        searchBox={{ ...searchBox, inputRef: sideSearchRef }}
      />

      <TaskListPane
        view={view}
        title={title}
        sub={sub}
        tasks={tasks}
        today={todo.today}
        cap={todo.cap}
        slotCount={view === 'tomorrow' ? counts.tomorrow : counts.today}
        rolledOver={rolloverDismissed ? 0 : rolled_over}
        onDismissRollover={() => setRolloverDismissed(true)}
        addInputRef={addInputRef}
        capBlocked={capBlocked}
        error={error}
        selectedId={selectedId}
        runningEntry={running_entry}
        onSelect={(task) => setSelectedId(task.id)}
        onContextMenu={(task, x, y) => setMenu({ task, x, y })}
        onToggle={toggleComplete}
        onStar={toggleImportant}
        onCreate={createTask}
        onReorder={reorder}
        onStopTimer={stopTimer}
        onOpenSidebar={() => setSidebarOpen(true)}
        // 中栏那个框只有待办池这一栏放（搜索态的 view 也是 backlog）。
        // 它挨着结果，「N 条」和 ✕ 就在眼睛已经在看的地方。
        searchBox={view === 'backlog' ? { ...searchBox, inputRef: listSearchRef } : null}
        search={todo.q ? { q: todo.q, meta: todo.meta ?? {} } : null}
        // headerExtra 原来挂的是 WorklogLinkPill（「N 条待关联」胶囊），随批量
        // 自动关联一起暂停了，见 config/recurring.yml。
      />

      {selected ? (
        <>
          {/* 窄屏下详情是全屏覆盖，给一层遮罩把它和背后的列表分开。
              sm: 之后详情是 330px 的侧滑面板，不需要遮罩。 */}
          <div
            onClick={() => setSelectedId(null)}
            className="fixed inset-0 z-10 bg-black/20 sm:hidden"
            aria-hidden="true"
          />
          {/* grid + min-h-0 是右栏能滚动的前提，两个都不能少。
              这一层 div 才是 grid item（中栏的 <main> 是自己直接当 grid item，
              所以它一直是好的）。少了 min-h-0，这层的自动最小尺寸就是
              min-content，行高被内容顶开 → 详情栏长到 2400px 也不出滚动条；
              少了 grid，里面的 <aside> 只是普通块级子元素、高度 auto，同样
              撑到内容高度 —— 那时 aside 上的 flex-1 + overflow-y-auto 永远
              没有受限高度，等于白写（给 aside 加 h-full 也救不了，因为行高
              本身就是被它顶开的，100% 只会算回 2400px）。
              用 grid 而不是 flex：grid 子元素默认双向 stretch，DetailPane
              不用改。 */}
          <div className="absolute inset-y-0 right-0 z-20 grid h-full min-h-0 w-full shadow-2xl sm:w-[min(330px,100%)] wide:static wide:z-auto wide:w-auto wide:shadow-none">
            <DetailPane
              task={selected}
              projects={projects}
              categories={categories}
              today={todo.today}
              capBlocked={capBlocked}
              error={error}
              runningEntry={running_entry}
              onClose={() => setSelectedId(null)}
              onToggle={toggleComplete}
              onPatch={patchTask}
              onOptimistic={patchOptimistic}
              onDestroy={destroyTask}
              onRefresh={refresh}
              onCreateStep={createStep}
              onLinkExisting={linkExisting}
              onUnlinkParent={unlinkParent}
              onToggleStep={toggleStep}
              onRenameStep={renameStep}
              onDeleteStep={deleteStep}
              onStartTimer={startTimer}
              onStopTimer={stopTimer}
              onHandoff={(task) => setHandoff({ kind: 'task', task })}
            />
          </div>
        </>
      ) : (
        <div />
      )}

      <AiHandoffSheet target={handoff} onClose={() => setHandoff(null)} />

      {/* 菜单渲染在这一层（页面级）而不是任务行里：中栏是自己的滚动容器，
          行内的浮层会被 overflow 裁掉。position: fixed 配视口坐标，所以挂在
          哪个节点下都一样，挂这里是为了让「谁拥有它」显而易见。 */}
      {menu && (
        <TaskContextMenu
          items={menuItems(menu.task)}
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
};

Todo.layout = (page: React.ReactNode) => <TodoLayout>{page}</TodoLayout>;

export default Todo;
